import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import EditableTable from "../../../reUsableTemplates/EditableTable/EditableTable";
import {
  getCertificateTypes,
  createCertificateType,
  updateCertificateType,
  deleteCertificateType,
  multiDeleteCertificateTypes,
} from "../../../../services/register_redux/register_actions/SnECertificate.action";
import into_icon from "../../../../assets/global_assets/create_data_imgs/into_icon.png";
import mainicon from "../../../../assets/global_assets/role_imgs/role-table-title-icon.png";
import CreateCertificateTemplate from './../../../global/Settings/modalForm/CreateCertificateTemplate';
const formatTypeLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

function CertificateTypes({ showSnackbar, setLoading }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState([]);
  const [editableRow, setEditableRow] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [actionChecked, setActionChecked] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");

  const header = [
    { key: "certificate_type", value: "Certificate Type", input: "text",readOnly: false,},
    { key: "created_by", value: "Created By", input: "text",readOnly: true,},
    { key: "updated_by", value: "Updated By", input: "text",readOnly: true,},
  ];

  // showSnackbar is re-created on every App render, so it is kept in a ref to
  // stop fetchdata from changing identity and re-triggering its effect.
  const showSnackbarRef = useRef(showSnackbar);
  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  const fetchdata = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(getCertificateTypes());
      if (result?.success && Array.isArray(result.data)) {
        const rows = result.data.map((row) => ({
          ...row,
          certificate_type_display: formatTypeLabel(row.certificate_type),
        }));
        setData(rows);
      } else {
        setData([]);
        if (result?.message) {
          showSnackbarRef.current(
            typeof result.message === "string"
              ? result.message
              : "Unable to load certificate types.",
            "warning"
          );
        }
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, setLoading]);

  useEffect(() => {
    fetchdata();
  }, [fetchdata]);

  useEffect(() => {
    if (location.state?.openCreate) {
      setOpenCreate(true);
      setNewTypeName("");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const handleSaveClick = async () => {
    if (!editedValues?.id) return;
    const name = (editedValues.certificate_type || "").trim();
    if (!name) {
      showSnackbar("Certificate type name is required.", "warning");
      return;
    }
    setLoading(true);
    try {
      const result = await dispatch(updateCertificateType(editedValues.id, name));
      if (result.success) {
        showSnackbar(result.message || "Updated successfully.", "success");
        setEditableRow(null);
        setEditedValues({});
        await fetchdata();
      } else {
        showSnackbar(result.message || "Update failed.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handledeleteClickOpenalert = async (id) => {
    setLoading(true);
    try {
      const result = await dispatch(deleteCertificateType(id));
      if (result.success) {
        showSnackbar(result.message || "Deleted successfully.", "success");
        setEditableRow(null);
        setActionChecked("delete");
        await fetchdata();
      } else {
        showSnackbar(result.message || "Delete failed.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitdelete = async (payload) => {
    const ids = payload?.ids || [];
    if (!ids.length) {
      showSnackbar("No rows selected.", "warning");
      return;
    }
    setLoading(true);
    try {
      const result = await dispatch(multiDeleteCertificateTypes(ids));
      if (result.success) {
        showSnackbar(result.message || "Deleted successfully.", "success");
        setActionChecked("delete");
        await fetchdata();
      } else {
        showSnackbar(result.message || "Delete failed.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const name = (newTypeName || "").trim();
    if (!name) {
      showSnackbar("Please enter a certificate type name.", "warning");
      return;
    }
    setLoading(true);
    try {
      const result = await dispatch(createCertificateType(name));
      if (result.success) {
        showSnackbar(result.message || "Created successfully.", "success");
        setOpenCreate(false);
        setNewTypeName("");
        setActionChecked("create");
        await fetchdata();
      } else {
        showSnackbar(result.message || "Create failed.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3 px-2">
        <div>
          <h4 className="mb-1 mx-3">Certificate Types</h4>
          <p className="text-muted mb-0 mx-3" style={{ fontSize: 13 }}>
            Create, edit, and delete certificate types used on the Certificate upload page.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => navigate("/certificate")}
        >
          Back to Certificate
        </button>
      </div>

      <EditableTable
        header={header}
        mainIcon={mainicon}
        title="Certificate Types"
        data={data}
        setEditableRow={setEditableRow}
        setEditedValues={setEditedValues}
        handledeleteClickOpenalert={handledeleteClickOpenalert}
        editableRow={editableRow}
        setLoading={setLoading}
        editedValues={editedValues}
        getEditPermission={true}
        getDeletePermission={true}
        date={false}
        submitdelete={submitdelete}
        handleSaveClick={handleSaveClick}
        showSnackbar={showSnackbar}
        actionChecked={actionChecked}
        setActionChecked={setActionChecked}
        hideColumnHeaderIcons={true}
        createOrfilterComp={
          <button type="button" onClick={() => setOpenCreate(true)}>
            <b>Create Certificate Type</b>
          </button>
        }
      />

      <CreateCertificateTemplate
        show={openCreate}
        onHide={() => setOpenCreate(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <div className="createCertificateTableMainContainer">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1 mt-2 mx-4">Create Certificate Type</h5>
              <p className="text-muted mb-0 mx-4">
                Create a new certificate type for use in the system.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-transparent btn-sm"
              onClick={() => setOpenCreate(false)}
              aria-label="Close"
            >
              <img src={into_icon} alt="" height={25} />
            </button>
          </div>

          <form onSubmit={handleCreateSubmit}>
            <div className="mb-3 px-3">
              <label className="form-label mx-2">
                <b>Type Name</b> <span className="text-danger">*</span>
              </label>

              <input
                className="form-control mx-2 mb-2"
                type="text"
                placeholder="e.g. S&E Certificate or Trade License"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                autoFocus
                required
              />

              <small className="text-muted mx-2">
                Example:
                <strong> Trade License</strong> → <strong>trade_license</strong>
              </small>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4 mx-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setOpenCreate(false)}
              >
                Cancel
              </button>

              <button type="submit" className="btn btn-primary">
                Save
              </button>
            </div>
          </form>
        </div>
      </CreateCertificateTemplate>
    </div>
  );
}

export default CertificateTypes;
